import { useEffect, useState, useRef, Component } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Calendar, CheckCircle2, XCircle, Star, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Tiny error boundary that prevents a crashing notification dropdown from blanking
// the entire page. If a single notification has malformed data and React throws,
// we fall back to a "Aucune notification" message rather than killing the app.
class NotifErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("NotificationBell crashed:", err, info); }
  render() {
    if (this.state.err) {
      return (
        <div className="p-8 text-center text-sm text-zinc-400" data-testid="notif-error-boundary">
          <Bell className="h-8 w-8 text-zinc-200 mx-auto mb-2" />
          Impossible d'afficher les notifications pour l'instant.
          <button onClick={() => this.setState({ err: null })} className="block mx-auto mt-3 text-xs text-emerald-600 hover:underline">Réessayer</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Coerce values that we render via JSX to safe strings — never render an object directly.
const safeStr = (v) => {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  try { return JSON.stringify(v); } catch { return ""; }
};

// Render either a router <Link> (for internal "/path") or a plain <a> (for absolute URLs).
// Some stored notifications may have absolute URLs from older deployments — react-router's
// <Link to> with an external URL behaves badly, so we route around it defensively.
const NotifLink = ({ href, onClick, children, "data-testid": testId }) => {
  if (!href) return <div onClick={onClick} data-testid={testId}>{children}</div>;
  const isExternal = /^(https?:|mailto:|tel:)/i.test(href);
  if (isExternal) {
    return <a href={href} onClick={onClick} data-testid={testId} rel="noopener noreferrer">{children}</a>;
  }
  return <Link to={href} onClick={onClick} data-testid={testId}>{children}</Link>;
};

const ICONS = {
  new_request: Calendar,
  request_approved: CheckCircle2,
  request_rejected: XCircle,
  booking_confirmed: DollarSign,
  rating_received: Star,
};

const ICON_COLORS = {
  new_request: "text-amber-600 bg-amber-100",
  request_approved: "text-emerald-600 bg-emerald-100",
  request_rejected: "text-red-600 bg-red-100",
  booking_confirmed: "text-emerald-600 bg-emerald-100",
  rating_received: "text-amber-600 bg-amber-100",
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/notifications");
      // Defensive: ensure items is an array, never null/object
      const safeItems = Array.isArray(data?.items) ? data.items.filter(n => n && typeof n === "object") : [];
      setItems(safeItems);
      setUnread(Number(data?.unread) || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const t = setInterval(fetchUnread, 60000); // poll every 60s
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`).catch(() => {});
    setItems(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAll = async () => {
    await api.put("/notifications/read-all").catch(() => {});
    setItems(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread(0);
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(v => !v)}
        className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-zinc-700" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="fixed sm:absolute top-16 sm:top-auto sm:mt-2 left-2 right-2 sm:left-auto sm:right-0 sm:w-[360px] max-w-[360px] mx-auto sm:mx-0 bg-white rounded-2xl shadow-xl border border-zinc-200 z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
            <h3 className="font-heading font-bold text-zinc-950">Notifications</h3>
            {unread > 0 && (
              <Button variant="ghost" size="sm" className="h-auto py-1 text-xs" onClick={markAll} data-testid="mark-all-read-btn">
                <CheckCheck className="h-3.5 w-3.5 mr-1" /> Tout marquer lu
              </Button>
            )}
          </div>
          <div className="max-h-[440px] overflow-y-auto">
            <NotifErrorBoundary>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-400" data-testid="no-notifications">
                <Bell className="h-8 w-8 text-zinc-200 mx-auto mb-2" />
                Aucune notification
              </div>
            ) : (
              items.map((n, i) => {
                const Icon = ICONS[n.type] || Bell;
                const colors = ICON_COLORS[n.type] || "text-zinc-600 bg-zinc-100";
                const when = (() => {
                  try {
                    if (!n.created_at) return "";
                    const d = new Date(n.created_at);
                    if (isNaN(d.getTime())) return "";
                    return formatDistanceToNow(d, { locale: fr, addSuffix: true });
                  } catch { return ""; }
                })();
                const content = (
                  <div className={`flex items-start gap-3 px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50/70 cursor-pointer transition-colors ${!n.is_read ? "bg-emerald-50/30" : ""}`}>
                    <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${colors}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-zinc-900 truncate">{safeStr(n.title)}</p>
                        {!n.is_read && <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{safeStr(n.message)}</p>
                      <p className="text-[10px] text-zinc-400 mt-1">{when}</p>
                    </div>
                  </div>
                );
                const linkHref = typeof n.link === "string" ? n.link : "";
                const key = n.id || `notif-${i}`;
                return (
                  <NotifLink
                    key={key}
                    href={linkHref}
                    onClick={() => { if (n.id && !n.is_read) markRead(n.id); setOpen(false); }}
                    data-testid={`notif-${i}`}
                  >
                    {content}
                  </NotifLink>
                );
              })
            )}
            </NotifErrorBoundary>
          </div>
        </div>
      )}
    </div>
  );
}
